import type { AnnualTerm, BillingPeriod } from '@/app/config/plans';

export type PricingVersion = 'web_v1' | 'web_v2';
export type PricingMode = 'legacy' | 'quoted' | 'paused';
export type ExecutionTarget =
  | 'subscription_create'
  | 'subscription_update'
  | 'subscription_demote'
  | 'payment_method_update';
export type PricingOperation =
  | 'initial'
  | 'manual_renewal'
  | 'plan_change'
  | 'reactivation'
  | 'auto_renew_restoration'
  | 'payment_method_update';
export interface PricingConfig {
  config_schema_version: 1;
  workflow_enabled: boolean;
  accepted_quotes_required: boolean;
  pricing_catalog_version: PricingVersion;
  clients: Record<'penzu_next' | 'legacy_frontend', { enabled: boolean; mode: PricingMode }>;
  quote_schema_versions: number[];
  quote_routes: string[];
  purchase_result_schema_versions: number[];
  billing_contract_schema_versions: number[];
}
export interface PricingCatalog {
  pricing_version: PricingVersion;
  currency: 'USD';
  prices: {
    pro: { monthly: { 1: number }; yearly: Record<AnnualTerm, number> };
    pro_plus: {
      monthly: { 1: number };
      yearly: Record<AnnualTerm, number>;
      lifetime: { 1: number };
    };
  };
}
export interface RenewalAdjustment {
  kind: 'discount' | 'add_on';
  amount_cents: number;
  quantity: number;
  never_expires: boolean;
  remaining_cycles: number | null;
}
interface FinancialTerms {
  currency: 'USD';
  tier: 'pro' | 'pro_plus';
  interval: BillingPeriod;
  term: AnnualTerm;
  list_price_cents: number;
  discount_cents: number;
  credit_cents: number;
  renewal_price_cents: number | null;
  renewal_interval: 'monthly' | 'yearly' | null;
  renewal_adjustments: RenewalAdjustment[];
}
export interface PricingQuote extends FinancialTerms {
  quote_schema_version: 2;
  quote_id: string;
  accepted_quote: string;
  expires_at: string;
  execution_target: ExecutionTarget;
  operation: PricingOperation;
  consent_required: boolean;
  price_source: 'active_catalog' | 'existing_contract';
  pricing_version: PricingVersion | null;
  due_now_cents: number;
  refund_cents: number;
  refunds: { source_payment_reference: string; amount_cents: number }[];
  promo_code: string | null;
  promo_attachment:
    | 'none'
    | 'upfront'
    | 'subscription'
    | 'remaining_agreement'
    | 'existing_agreement';
  discount_billing_cycles: number;
  estimated_renewal_starts_on: string | null;
}
export interface ConfirmedPurchase extends FinancialTerms {
  purchase_schema_version: 1;
  purchase_id: string;
  completed_at: string;
  operation: Exclude<PricingOperation, 'payment_method_update'>;
  purchase_kind: 'initial' | 'manual_renewal' | 'plan_change' | 'reactivation';
  purchase_pricing_version: PricingVersion | null;
  subscription_pricing_version: PricingVersion | null;
  billing_provider: 'braintree';
  charged_amount_cents: number;
  refunded_amount_cents: number;
  refunds: { source_payment_reference: string; refund_reference: string; amount_cents: number }[];
  payment_reference: string | null;
  renewal_starts_on: string | null;
}
export interface BillingContract {
  billing_contract_schema_version: 1;
  status: 'verified' | 'unavailable' | 'not_applicable' | 'external';
  reason:
    | 'missing_contract'
    | 'remote_contract_not_found'
    | 'unresolved_contract'
    | 'contract_lookup_unavailable'
    | null;
  billing_provider: 'braintree' | 'paypal' | 'ios' | 'android' | null;
  pricing_version: PricingVersion | null;
  currency: 'USD' | null;
  tier: 'pro' | 'pro_plus' | null;
  interval: BillingPeriod | null;
  auto_renew: boolean | null;
  agreement_status: 'active' | 'pending' | 'past_due' | 'canceled' | 'expired' | null;
  renewal_price_cents: number | null;
  renewal_interval: 'monthly' | 'yearly' | null;
  next_billing_period_amount_cents: number | null;
  next_billing_on: string | null;
  balance_cents: number | null;
  renewal_adjustments: RenewalAdjustment[] | null;
  verified_at: string | null;
}
export interface AttemptStatus {
  attempt_id: string;
  status: 'pending' | 'completed' | 'needs_review' | 'released';
  new_attempt_allowed: boolean;
}
export interface CompletedResult {
  attempt_id: string;
  status: 'completed';
  purchase: ConfirmedPurchase | null;
}
export interface PricingErrorBody {
  title: string;
  message: string;
  code?: string;
  attempt_id?: string;
  replacement_quote?: PricingQuote;
}
export class PricingApiError extends Error {
  constructor(
    public status: number,
    public detail: PricingErrorBody,
    public retryAfterSeconds?: number,
  ) {
    super(detail.message);
    this.name = 'PricingApiError';
  }
}
